import React, { useState, useEffect, useRef } from 'react';
import { theme } from '../theme';
import { Spinner, Badge, Select, Button } from './ui';
import Icons from './Icons';
import { exportCSV, exportJSON } from '../utils/export';

// ---------------------------------------------------------------------------
// Results Table
// ---------------------------------------------------------------------------
const thStyle = {
  position: 'sticky', top: 0, padding: '8px 12px', textAlign: 'left',
  background: theme.surface, borderBottom: `2px solid ${theme.border}`,
  color: theme.text, fontWeight: 600, fontSize: '11px', textTransform: 'uppercase',
  letterSpacing: '0.5px', whiteSpace: 'nowrap', zIndex: 1,
};
const tdStyle = {
  padding: '7px 12px', borderBottom: `1px solid ${theme.border}11`,
  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
  fontSize: '13px',
};

const ROW_NUM_WIDTH = 44;
const MIN_COL_WIDTH = 90;
const MAX_COL_WIDTH = 720;
const DEFAULT_COL_WIDTH = 180;
const AUTO_FIT_SAMPLE_SIZE = 3000;

function clampWidth(px) {
  return Math.max(MIN_COL_WIDTH, Math.min(MAX_COL_WIDTH, px));
}

export default function ResultsTable({ columns, rows, rawXml, error, loading, validating, isConnected = true, connStatus, canRetryAi, onRetryAi, aiRetrying }) {
  const [viewMode, setViewMode] = useState('table');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [sortCol, setSortCol] = useState(null);
  const [sortDir, setSortDir] = useState(null); // 'asc' | 'desc' | null
  const [filters, setFilters] = useState({}); // { COL_NAME: 'filter text' }
  const [showFilters, setShowFilters] = useState(false);
  const [columnWidths, setColumnWidths] = useState({});
  const [resizing, setResizing] = useState(null); // { col, startX, startWidth }
  const measureCanvasRef = useRef(null);

  // Reset state when results change
  useEffect(() => {
    setPage(0);
    setSortCol(null);
    setSortDir(null);
    setFilters({});
  }, [rows]);

  useEffect(() => {
    setColumnWidths((prev) => {
      const next = {};
      columns.forEach((col) => {
        next[col] = prev[col] || clampWidth(Math.max(DEFAULT_COL_WIDTH, col.length * 11 + 40));
      });
      return next;
    });
  }, [columns]);

  useEffect(() => {
    if (!resizing) return;
    const handleMove = (e) => {
      const width = clampWidth(resizing.startWidth + (e.clientX - resizing.startX));
      setColumnWidths((prev) => ({ ...prev, [resizing.col]: width }));
    };
    const handleUp = () => {
      setResizing(null);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    document.addEventListener('mousemove', handleMove);
    document.addEventListener('mouseup', handleUp);
    return () => {
      document.removeEventListener('mousemove', handleMove);
      document.removeEventListener('mouseup', handleUp);
    };
  }, [resizing]);

  if (loading || validating) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: theme.textMuted }}>
        <Spinner />
        <p style={{ marginTop: '12px' }}>{validating ? 'Validating SQL…' : 'Executing query…'}</p>
      </div>
    );
  }
  if (error) {
    // Normalise: accept legacy string errors or new structured object
    const isObj      = typeof error === 'object' && error !== null;
    const message    = isObj ? (error.message    || 'An error occurred') : error;
    const errorType  = isObj ? (error.errorType  || 'unknown') : 'unknown';
    const oracleCode = isObj ? (error.oracleCode || null) : null;
    const detail     = isObj ? (error.detail     || null) : null;

    const TYPE_LABELS = {
      auth:         'Authentication Error',
      sql_error:    'SQL Error',
      sql_syntax:   'SQL Syntax Error',
      timeout:      'Query Timeout',
      server_error: 'Server Error',
      unknown:      'Error',
    };

    const TYPE_HINTS = {
      auth:         'Open the Connections panel and verify your username and password.',
      sql_error:    'Review your SQL syntax and verify that table and column names exist.',
      sql_syntax:   null, // hint is embedded in the detail for sql_syntax
      timeout:      'Try simplifying the query or adding a more specific WHERE clause.',
      server_error: 'This may be caused by a SQL syntax error, a connection issue, or a temporary Oracle Fusion outage. Check your SQL first, then verify the connection URL.',
      unknown:      null,
    };

    const label = TYPE_LABELS[errorType] || 'Error';
    const hint  = TYPE_HINTS[errorType]  || null;

    return (
      <div style={{
        padding: '16px',
        background: `${theme.danger}12`,
        border: `1px solid ${theme.danger}40`,
        borderRadius: '8px',
      }}>
        {/* Header row */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
          <span style={{ color: theme.danger, fontWeight: 700, fontSize: '13px' }}>⚠ {label}</span>
          {oracleCode && (
            <span style={{
              fontSize: '11px', fontWeight: 700,
              color: theme.warning,
              background: `${theme.warning}20`,
              border: `1px solid ${theme.warning}50`,
              borderRadius: '4px', padding: '1px 7px',
            }}>
              {oracleCode}
            </span>
          )}
          {canRetryAi && errorType === 'sql_error' && onRetryAi && (
            <button
              onClick={onRetryAi}
              disabled={aiRetrying}
              title="Resubmit this SQL with the error context to regenerate a corrected query"
              style={{
                marginLeft: 'auto',
                display: 'inline-flex', alignItems: 'center', gap: '6px',
                padding: '4px 10px', fontSize: '12px', fontWeight: 600,
                background: aiRetrying ? `${theme.accent}33` : `${theme.accent}22`,
                color: theme.accent,
                border: `1px solid ${theme.accent}66`,
                borderRadius: '6px',
                cursor: aiRetrying ? 'wait' : 'pointer',
              }}
            >
              {aiRetrying ? <Spinner /> : <span>⚡</span>}
              {aiRetrying ? 'Retrying…' : 'Retry with AI'}
            </button>
          )}
        </div>

        {/* Main message */}
        <div style={{ color: theme.text, fontSize: '13px', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
          {message}
        </div>

        {/* Hint */}
        {hint && (
          <div style={{
            marginTop: '10px', padding: '8px 12px',
            background: `${theme.accent}10`,
            border: `1px solid ${theme.accent}30`,
            borderRadius: '6px',
            fontSize: '12px', color: theme.textMuted,
            display: 'flex', gap: '6px',
          }}>
            <span style={{ flexShrink: 0 }}>💡</span>
            <span>{hint}</span>
          </div>
        )}

        {/* For sql_syntax: show hints inline (they're user-friendly, not technical) */}
        {detail && errorType === 'sql_syntax' && (
          <pre style={{
            marginTop: '10px',
            padding: '10px 12px',
            background: theme.codeBg,
            border: `1px solid ${theme.border}`,
            borderRadius: '6px',
            color: theme.textMuted,
            fontSize: '12px',
            whiteSpace: 'pre-wrap',
            fontFamily: "'SF Mono', 'Fira Code', monospace",
          }}>
            {detail}
          </pre>
        )}

        {/* For all other errors: collapsible technical detail */}
        {detail && detail !== message && errorType !== 'sql_syntax' && (
          <details style={{ marginTop: '10px' }}>
            <summary style={{
              color: theme.textMuted, fontSize: '12px', cursor: 'pointer',
              userSelect: 'none', listStyle: 'none',
              display: 'flex', alignItems: 'center', gap: '4px',
            }}>
              <span style={{ fontSize: '10px' }}>▶</span> Technical details
            </summary>
            <pre style={{
              marginTop: '8px',
              padding: '10px 12px',
              background: theme.codeBg,
              border: `1px solid ${theme.border}`,
              borderRadius: '6px',
              color: theme.textMuted,
              fontSize: '11px',
              whiteSpace: 'pre-wrap',
              fontFamily: "'SF Mono', 'Fira Code', monospace",
              overflowX: 'auto',
            }}>
              {detail}
            </pre>
          </details>
        )}
      </div>
    );
  }
  if (!columns || columns.length === 0) {
    if (!isConnected) {
      return (
        <div style={{ padding: '40px', textAlign: 'center', color: theme.textMuted }}>
          <Icons.Database />
          <p style={{ marginTop: '8px', fontSize: '13px', color: theme.text }}>No connection configured</p>
          <p style={{ marginTop: '4px', fontSize: '12px' }}>
            Open <strong style={{ color: theme.text }}>Connections</strong> in the sidebar to add one.
          </p>
        </div>
      );
    }
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: theme.textMuted }}>
        <Icons.Database />
        <p style={{ marginTop: '8px', fontSize: '13px' }}>Run a query to see results</p>
      </div>
    );
  }

  // --- Filter rows ---
  const activeFilters = Object.entries(filters).filter(([, v]) => v.trim());
  let processedRows = rows;
  if (activeFilters.length > 0) {
    processedRows = rows.filter((row) =>
      activeFilters.every(([col, filterVal]) => {
        const cellVal = String(row[col] ?? '').toLowerCase();
        return cellVal.includes(filterVal.toLowerCase());
      })
    );
  }

  // --- Sort rows ---
  if (sortCol && sortDir) {
    processedRows = [...processedRows].sort((a, b) => {
      const aVal = a[sortCol] ?? '';
      const bVal = b[sortCol] ?? '';
      // Try numeric comparison
      const aNum = Number(aVal);
      const bNum = Number(bVal);
      if (!isNaN(aNum) && !isNaN(bNum) && aVal !== '' && bVal !== '') {
        return sortDir === 'asc' ? aNum - bNum : bNum - aNum;
      }
      // String comparison
      const aStr = String(aVal).toLowerCase();
      const bStr = String(bVal).toLowerCase();
      if (aStr < bStr) return sortDir === 'asc' ? -1 : 1;
      if (aStr > bStr) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  }

  const totalRows = processedRows.length;
  const totalPages = Math.ceil(totalRows / pageSize);
  const startIdx = page * pageSize;
  const endIdx = Math.min(startIdx + pageSize, totalRows);
  const pageRows = processedRows.slice(startIdx, endIdx);

  // Handle sort click: cycle through asc -> desc -> none
  const handleSort = (col) => {
    if (sortCol !== col) {
      setSortCol(col);
      setSortDir('asc');
    } else if (sortDir === 'asc') {
      setSortDir('desc');
    } else {
      setSortCol(null);
      setSortDir(null);
    }
    setPage(0);
  };

  const startResize = (col, e) => {
    e.preventDefault();
    e.stopPropagation();
    const startWidth = columnWidths[col] || DEFAULT_COL_WIDTH;
    setResizing({ col, startX: e.clientX, startWidth });
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  };

  const measureText = (text, font) => {
    if (!measureCanvasRef.current && typeof document !== 'undefined') {
      measureCanvasRef.current = document.createElement('canvas');
    }
    const canvas = measureCanvasRef.current;
    if (!canvas) return String(text || '').length * 8;
    const ctx = canvas.getContext('2d');
    if (!ctx) return String(text || '').length * 8;
    ctx.font = font;
    return ctx.measureText(String(text ?? '')).width;
  };

  const autoFitColumn = (col, e) => {
    e.preventDefault();
    e.stopPropagation();

    const headerWidth = measureText(col, "600 11px -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial, sans-serif");
    const sampleSize = Math.min(processedRows.length, AUTO_FIT_SAMPLE_SIZE);
    let maxWidth = headerWidth;
    for (let i = 0; i < sampleSize; i += 1) {
      const cellWidth = measureText(processedRows[i]?.[col], "400 13px -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial, sans-serif");
      if (cellWidth > maxWidth) maxWidth = cellWidth;
    }
    // Add room for sort icon, cell padding, and resize handle hit area.
    const fitted = clampWidth(Math.ceil(maxWidth + 44));
    setColumnWidths((prev) => ({ ...prev, [col]: fitted }));
  };

  const updateFilter = (col, val) => {
    setFilters((prev) => ({ ...prev, [col]: val }));
    setPage(0);
  };

  const clearAllFilters = () => {
    setFilters({});
    setPage(0);
  };

  const pageSizeOptions = [
    { value: '25', label: '25 / page' },
    { value: '50', label: '50 / page' },
    { value: '100', label: '100 / page' },
    { value: '200', label: '200 / page' },
    { value: '500', label: '500 / page' },
  ];

  const PaginationButton = ({ children, onClick, disabled, active }) => {
    const [hovered, setHovered] = useState(false);
    return (
      <button onClick={onClick} disabled={disabled}
        onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}
        style={{
          padding: '3px 8px', border: 'none', borderRadius: '4px', cursor: disabled ? 'default' : 'pointer',
          fontSize: '12px', fontWeight: active ? 600 : 400, minWidth: '28px',
          background: active ? theme.accent : hovered && !disabled ? theme.surfaceHover : 'transparent',
          color: active ? '#fff' : disabled ? theme.border : theme.textMuted,
          transition: 'all 0.12s',
        }}>
        {children}
      </button>
    );
  };

  const pageButtons = () => {
    const btns = [];
    const maxVisible = 5;
    let start = Math.max(0, page - Math.floor(maxVisible / 2));
    let end = Math.min(totalPages, start + maxVisible);
    if (end - start < maxVisible) start = Math.max(0, end - maxVisible);
    if (start > 0) {
      btns.push(<PaginationButton key={0} onClick={() => setPage(0)}>1</PaginationButton>);
      if (start > 1) btns.push(<span key="e1" style={{ color: theme.textMuted, fontSize: '12px', padding: '0 2px' }}>...</span>);
    }
    for (let i = start; i < end; i++) {
      btns.push(<PaginationButton key={i} onClick={() => setPage(i)} active={i === page}>{i + 1}</PaginationButton>);
    }
    if (end < totalPages) {
      if (end < totalPages - 1) btns.push(<span key="e2" style={{ color: theme.textMuted, fontSize: '12px', padding: '0 2px' }}>...</span>);
      btns.push(<PaginationButton key={totalPages - 1} onClick={() => setPage(totalPages - 1)}>{totalPages}</PaginationButton>);
    }
    return btns;
  };

  const SortIcon = ({ col }) => {
    if (sortCol === col) {
      return sortDir === 'asc' ? <Icons.SortAsc /> : <Icons.SortDesc />;
    }
    return <Icons.SortNone />;
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, overflow: 'hidden' }}>
      {viewMode === 'table' ? (
        <>
          {/* Scrollable table area */}
          <div style={{
            flex: 1, overflow: 'auto', minHeight: 0,
            border: `1px solid ${theme.border}`, borderRadius: '8px',
            background: theme.surface,
          }}>
            <table style={{ width: 'max-content', minWidth: '100%', borderCollapse: 'collapse' }}>
              <thead>
                {/* Column headers with sort */}
                <tr>
                  <th style={{ ...thStyle, width: `${ROW_NUM_WIDTH}px`, minWidth: `${ROW_NUM_WIDTH}px`, maxWidth: `${ROW_NUM_WIDTH}px`, color: theme.textMuted, textAlign: 'center' }}>#</th>
                  {columns.map((col) => (
                    <th
                      key={col}
                      style={{
                        ...thStyle,
                        cursor: 'pointer',
                        userSelect: 'none',
                        width: `${columnWidths[col] || DEFAULT_COL_WIDTH}px`,
                        minWidth: `${columnWidths[col] || DEFAULT_COL_WIDTH}px`,
                        maxWidth: `${columnWidths[col] || DEFAULT_COL_WIDTH}px`,
                      }}
                      onClick={() => handleSort(col)}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px', position: 'relative', paddingRight: '10px' }}>
                        <span style={{ flex: 1 }}>{col}</span>
                        <span style={{ color: sortCol === col ? theme.accent : theme.textMuted, opacity: sortCol === col ? 1 : 0.4 }}>
                          <SortIcon col={col} />
                        </span>
                        <button
                          type="button"
                          aria-label={`Resize ${col} column`}
                          title="Drag to resize. Double-click to fit content."
                          onMouseDown={(e) => startResize(col, e)}
                          onDoubleClick={(e) => autoFitColumn(col, e)}
                          onClick={(e) => e.stopPropagation()}
                          style={{
                            position: 'absolute',
                            top: 0,
                            right: -6,
                            width: '12px',
                            height: '100%',
                            border: 'none',
                            background: 'transparent',
                            boxShadow: `inset 1px 0 0 ${theme.border}`,
                            cursor: 'col-resize',
                            padding: 0,
                          }}
                        />
                      </div>
                    </th>
                  ))}
                </tr>
                {/* Filter row */}
                {showFilters && (
                  <tr>
                    <th style={{ ...thStyle, padding: '4px 6px', borderBottom: `1px solid ${theme.border}` }}>
                      <button onClick={clearAllFilters} title="Clear all filters"
                        style={{ background: 'none', border: 'none', color: theme.textMuted, cursor: 'pointer', padding: '2px', fontSize: '10px' }}>
                        <Icons.X />
                      </button>
                    </th>
                    {columns.map((col) => (
                      <th
                        key={`f-${col}`}
                        style={{
                          ...thStyle,
                          padding: '4px 6px',
                          borderBottom: `1px solid ${theme.border}`,
                          width: `${columnWidths[col] || DEFAULT_COL_WIDTH}px`,
                          minWidth: `${columnWidths[col] || DEFAULT_COL_WIDTH}px`,
                          maxWidth: `${columnWidths[col] || DEFAULT_COL_WIDTH}px`,
                        }}
                      >
                        <input
                          value={filters[col] || ''}
                          onChange={(e) => updateFilter(col, e.target.value)}
                          placeholder="Filter..."
                          style={{
                            width: '100%', padding: '3px 6px', fontSize: '11px',
                            background: theme.codeBg, border: `1px solid ${filters[col] ? theme.accent : theme.border}`,
                            borderRadius: '4px', color: theme.text, outline: 'none',
                            fontWeight: 400, textTransform: 'none', letterSpacing: 'normal',
                          }}
                          onFocus={(e) => (e.target.style.borderColor = theme.borderFocus)}
                          onBlur={(e) => (e.target.style.borderColor = filters[col] ? theme.accent : theme.border)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </th>
                    ))}
                  </tr>
                )}
              </thead>
              <tbody>
                {pageRows.map((row, i) => (
                  <tr key={startIdx + i} style={{ background: i % 2 === 0 ? 'transparent' : `${theme.surface}80` }}>
                    <td style={{ ...tdStyle, color: theme.textMuted, fontSize: '11px', textAlign: 'center', width: `${ROW_NUM_WIDTH}px`, minWidth: `${ROW_NUM_WIDTH}px`, maxWidth: `${ROW_NUM_WIDTH}px` }}>{startIdx + i + 1}</td>
                    {columns.map((col) => (
                      <td
                        key={col}
                        style={{
                          ...tdStyle,
                          width: `${columnWidths[col] || DEFAULT_COL_WIDTH}px`,
                          minWidth: `${columnWidths[col] || DEFAULT_COL_WIDTH}px`,
                          maxWidth: `${columnWidths[col] || DEFAULT_COL_WIDTH}px`,
                        }}
                        title={row[col] == null ? '' : String(row[col])}
                      >
                        {row[col]}
                      </td>
                    ))}
                  </tr>
                ))}
                {pageRows.length === 0 && (
                  <tr>
                    <td colSpan={columns.length + 1} style={{ ...tdStyle, textAlign: 'center', color: theme.textMuted, padding: '20px' }}>
                      No rows match the current filters
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Footer: filter toggle + pagination + export */}
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '8px 0 0', flexShrink: 0,
          }}>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <button onClick={() => setShowFilters((v) => !v)}
                style={{
                  display: 'flex', alignItems: 'center', gap: '4px', padding: '3px 8px',
                  border: `1px solid ${showFilters ? theme.accent : theme.border}`,
                  borderRadius: '5px', cursor: 'pointer', fontSize: '11px', fontWeight: 500,
                  background: showFilters ? `${theme.accent}18` : 'transparent',
                  color: showFilters ? theme.accent : theme.textMuted,
                  transition: 'all 0.15s',
                }}
              >
                <Icons.Filter />
                Filter
                {activeFilters.length > 0 && (
                  <span style={{
                    background: theme.accent, color: '#fff', borderRadius: '50%',
                    width: '14px', height: '14px', fontSize: '9px', fontWeight: 700,
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    {activeFilters.length}
                  </span>
                )}
              </button>
              <Badge color={theme.success}>
                {activeFilters.length > 0 ? `${totalRows}/${rows.length}` : totalRows} row{totalRows !== 1 ? 's' : ''}
              </Badge>
              <Badge>{columns.length} col{columns.length !== 1 ? 's' : ''}</Badge>
              {totalRows > pageSize && (
                <span style={{ fontSize: '11px', color: theme.textMuted }}>
                  Showing {startIdx + 1}-{endIdx}
                </span>
              )}
            </div>

            {totalPages > 1 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                <PaginationButton onClick={() => setPage(Math.max(0, page - 1))} disabled={page === 0}>‹</PaginationButton>
                {pageButtons()}
                <PaginationButton onClick={() => setPage(Math.min(totalPages - 1, page + 1))} disabled={page >= totalPages - 1}>›</PaginationButton>
              </div>
            )}

            <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
              <div style={{ display: 'flex', background: theme.bg, borderRadius: '6px', padding: '2px' }}>
                {['table', 'xml'].map((m) => (
                  <button key={m} onClick={() => setViewMode(m)} style={{
                    padding: '3px 8px', border: 'none', borderRadius: '5px', cursor: 'pointer',
                    fontSize: '11px', fontWeight: viewMode === m ? 600 : 400,
                    background: viewMode === m ? theme.surface : 'transparent',
                    color: viewMode === m ? theme.text : theme.textMuted,
                    transition: 'all 0.15s', textTransform: 'capitalize',
                  }}>{m}</button>
                ))}
              </div>
              <Button variant="ghost" size="sm" icon={<Icons.Download />} onClick={() => exportCSV(columns, processedRows)}>CSV</Button>
              <Button variant="ghost" size="sm" icon={<Icons.Download />} onClick={() => exportJSON(columns, processedRows)}>JSON</Button>
            </div>
          </div>
        </>
      ) : (
        <>
          <pre style={{
            flex: 1, background: theme.codeBg, border: `1px solid ${theme.border}`, borderRadius: '8px',
            padding: '14px', fontSize: '12px', overflow: 'auto', minHeight: 0,
            fontFamily: 'monospace', color: theme.textMuted,
          }}>
            {rawXml || 'No XML data available'}
          </pre>
          <div style={{
            display: 'flex', justifyContent: 'flex-end', alignItems: 'center',
            padding: '8px 0 0', flexShrink: 0,
          }}>
            <div style={{ display: 'flex', background: theme.bg, borderRadius: '6px', padding: '2px' }}>
              {['table', 'xml'].map((m) => (
                <button key={m} onClick={() => setViewMode(m)} style={{
                  padding: '3px 8px', border: 'none', borderRadius: '5px', cursor: 'pointer',
                  fontSize: '11px', fontWeight: viewMode === m ? 600 : 400,
                  background: viewMode === m ? theme.surface : 'transparent',
                  color: viewMode === m ? theme.text : theme.textMuted,
                  transition: 'all 0.15s', textTransform: 'capitalize',
                }}>{m}</button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
